/*
 * Lakeland Lights - Scent Deck client (Phase 2)
 * Shared by the scent story card pages and the My Scents deck page.
 *
 * Storage: localStorage key llsd.favs.v2
 *   { v: 2, owner: null | "<customerId>", items: [{ slug, gid, at }] }
 *
 * Ownership rules (build brief section 2.4) - the shared-device guarantee:
 *   owner null,  current null  -> guest session, hydrate from local items
 *   owner null,  current A     -> merge guest items to server (skip merge if empty), owner = A
 *   owner A,     current A     -> server truth wins: list, mirror, hydrate
 *   owner A,     current null  -> logout detected: WIPE items, owner = null
 *   owner A,     current B     -> account switch: WIPE first, then list for B. NEVER merge.
 * Merge only ever consumes guest-owned local data (owner === null).
 *
 * Availability states in buyState() mirror brief sections 2.5-2.6 and must stay
 * in sync with the Liquid logic in sections/scent-story-card.liquid.
 */
(function () {
  'use strict';

  var KEY = 'llsd.favs.v2';
  var V1KEY = 'llsd.favs.v1';
  var PROXY = '/apps/scent-deck';

  var HEART_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21c-4.5-3.6-8-6.6-8-10.2C4 8 6 6 8.5 6c1.5 0 2.7.7 3.5 1.8C12.8 6.7 14 6 15.5 6 18 6 20 8 20 10.8c0 3.6-3.5 6.6-8 10.2z"/></svg>';

  /* ---------------- storage ---------------- */

  function storageOK() {
    try {
      localStorage.setItem('llsd.test', '1');
      localStorage.removeItem('llsd.test');
      return true;
    } catch (e) {
      return false;
    }
  }

  function emptyState() {
    return { v: 2, owner: null, items: [] };
  }

  function validItem(i) {
    return i && typeof i.gid === 'string' && typeof i.slug === 'string';
  }

  function readState() {
    if (!storageOK()) return emptyState();
    try {
      var raw = localStorage.getItem(KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (s && s.v === 2 && Array.isArray(s.items)) {
          s.items = s.items.filter(validItem);
          return s;
        }
      }
      return migrateV1();
    } catch (e) {
      return emptyState();
    }
  }

  function writeState(s) {
    if (!storageOK()) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch (e) {
      /* quota or private mode mid-session: server still holds logged-in saves */
    }
  }

  /* v1 never shipped; belt and braces per the brief. Guest-owned only if mergedFor was null. */
  function migrateV1() {
    var out = emptyState();
    try {
      var raw = localStorage.getItem(V1KEY);
      if (!raw) return out;
      var v1 = JSON.parse(raw);
      localStorage.removeItem(V1KEY);
      if (v1 && v1.mergedFor == null && Array.isArray(v1.items)) {
        out.items = v1.items.filter(validItem);
        writeState(out);
      }
    } catch (e) {}
    return out;
  }

  function stampItems(items) {
    var now = new Date().toISOString();
    return (items || []).filter(validItem).map(function (i) {
      return { slug: i.slug, gid: i.gid, at: i.at || now };
    });
  }

  function stripItems(items) {
    return (items || []).filter(validItem).map(function (i) {
      return { gid: i.gid, slug: i.slug };
    });
  }

  function localAdd(item) {
    var s = readState();
    var exists = s.items.some(function (i) { return i.gid === item.gid; });
    if (!exists) {
      s.items.push({ slug: item.slug, gid: item.gid, at: new Date().toISOString() });
      writeState(s);
    }
  }

  function localRemove(gid) {
    var s = readState();
    s.items = s.items.filter(function (i) { return i.gid !== gid; });
    writeState(s);
  }

  function localHas(gid) {
    return readState().items.some(function (i) { return i.gid === gid; });
  }

  /* ---------------- proxy API (same-origin; Shopify signs the proxy request) ---------------- */

  function api(path, body) {
    var opts;
    if (body) {
      opts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      };
    }
    return fetch(PROXY + path, opts).then(function (r) {
      if (!r.ok) throw new Error('proxy ' + r.status);
      return r.json();
    });
  }

  /* ---------------- ownership reconcile (runs once per page load, before hydration) ---------------- */

  function reconcile(current) {
    var s = readState();

    /* guest, guest-owned data: hydrate locally */
    if (s.owner === null && current === null) {
      return Promise.resolve({ items: s.items });
    }

    /* guest-owned data, now logged in: merge (or plain list when empty - an empty
       local list must never modify the server list). owner is only set on success,
       so a failed merge retries on the next load. */
    if (s.owner === null && current !== null) {
      var call = s.items.length
        ? api('/merge', { items: stripItems(s.items) })
        : api('/list');
      return call.then(function (res) {
        var st = { v: 2, owner: current, items: stampItems(res.items) };
        writeState(st);
        return { items: st.items };
      });
    }

    /* same account: server truth wins, mirror it */
    if (s.owner === current) {
      return api('/list').then(function (res) {
        var st = { v: 2, owner: current, items: stampItems(res.items) };
        writeState(st);
        return { items: st.items };
      });
    }

    /* logout detected: wipe, clean guest state */
    if (current === null) {
      writeState(emptyState());
      return Promise.resolve({ items: [] });
    }

    /* account switch: wipe FIRST, then list for the new account. Never merge. */
    writeState(emptyState());
    return api('/list').then(function (res) {
      var st = { v: 2, owner: current, items: stampItems(res.items) };
      writeState(st);
      return { items: st.items };
    });
  }

  /* ---------------- availability states (brief 2.5-2.6; mirrors the Liquid) ---------------- */

  function lowerFirst(str) {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  function buyState(card) {
    var candleOk = !!(card.candle && card.candle.available && card.candle.url);
    var meltOk = !!(card.melt && card.melt.available && card.melt.url);
    var out = { links: [], line: null, quiet: false };
    if (candleOk) out.links.push({ href: card.candle.url, label: 'Order the candle' });
    if (meltOk) out.links.push({ href: card.melt.url, label: 'Order the wax melt' });
    if (!candleOk && !meltOk) {
      if (card.seasonalNote) {
        out.line = card.seasonalNote + '. Itʼs in your saved scents - order it the day it returns.';
      } else {
        out.line = 'Back soon';
        out.quiet = true;
      }
    } else if (card.seasonalNote) {
      if (!candleOk) out.line = 'The candle is ' + lowerFirst(card.seasonalNote);
      if (!meltOk) out.line = 'The wax melt is ' + lowerFirst(card.seasonalNote);
    }
    return out;
  }

  /* ---------------- card page ---------------- */

  function initCard(article) {
    var gid = article.getAttribute('data-scent-gid');
    var slug = article.getAttribute('data-scent-slug');
    var customerId = article.getAttribute('data-customer-id') || null;
    var deckUrl = article.getAttribute('data-deck-url') || '/pages/my-scents';
    var btn = article.querySelector('[data-save-btn]');
    var label = article.querySelector('[data-save-label]');
    var note = article.querySelector('[data-save-note]');
    var accountLine = article.querySelector('[data-account-line]');
    if (!btn || !gid || !slug) return;

    /* Row 13: no localStorage and no login -> the account prompt is the graceful fallback */
    var canPersist = storageOK() || customerId !== null;

    btn.hidden = false;

    function setHeart(on) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (label) label.textContent = on ? 'Saved' : 'Save';
    }

    function showSavedNote() {
      if (!note) return;
      note.textContent = 'Saved. ';
      var a = document.createElement('a');
      a.href = deckUrl;
      a.textContent = 'See my scents.';
      note.appendChild(a);
      note.hidden = false;
    }

    function showNoteText(text) {
      if (!note) return;
      note.textContent = text;
      note.hidden = false;
    }

    function hideNote() {
      if (!note) return;
      note.textContent = '';
      note.hidden = true;
    }

    reconcile(customerId)
      .then(function (res) {
        setHeart(res.items.some(function (i) { return i.gid === gid; }));
      })
      .catch(function () {
        /* reconcile fetch failed: hydrate from the local mirror (wipes already applied) */
        setHeart(localHas(gid));
      });

    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      if (!canPersist) {
        showNoteText('Create an account to keep your scents on any device.');
        if (accountLine) accountLine.classList.add('scent-card__account-line--strong');
        return;
      }

      var wasOn = btn.getAttribute('aria-pressed') === 'true';
      var nextOn = !wasOn;

      /* optimistic heart + local mirror */
      setHeart(nextOn);
      if (nextOn) {
        localAdd({ gid: gid, slug: slug });
      } else {
        localRemove(gid);
      }

      if (customerId === null) {
        if (nextOn) { showSavedNote(); } else { hideNote(); }
        return;
      }

      /* logged in: single-flight per card so the final state matches the final tap */
      btn.disabled = true;
      api(nextOn ? '/save' : '/remove', { gid: gid, slug: slug })
        .then(function () {
          if (nextOn) { showSavedNote(); } else { hideNote(); }
        })
        .catch(function () {
          /* revert heart and mirror, quiet retry message */
          setHeart(wasOn);
          if (wasOn) {
            localAdd({ gid: gid, slug: slug });
          } else {
            localRemove(gid);
          }
          showNoteText('Could not save - tap to retry');
        })
        .then(function () {
          btn.disabled = false;
        });
    });
  }

  /* ---------------- deck page ---------------- */

  function initDeck(root) {
    var customerId = root.getAttribute('data-customer-id') || null;
    var grid = root.querySelector('[data-deck-grid]');
    var status = root.querySelector('[data-deck-status]');
    var emptyEl = root.querySelector('[data-deck-empty]');
    var indexEl = document.querySelector('[data-deck-index]');
    if (!grid || !indexEl) return;

    var index;
    try {
      index = JSON.parse(indexEl.textContent);
    } catch (e) {
      return;
    }
    var byGid = {};
    index.forEach(function (c) { byGid[c.gid] = c; });

    function setStatus(text) {
      if (!status) return;
      if (text) {
        status.textContent = text;
        status.hidden = false;
      } else {
        status.textContent = '';
        status.hidden = true;
      }
    }

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text) n.textContent = text;
      return n;
    }

    function makeTile(card, items) {
      var tile = el('article', 'scent-deck__tile');
      tile.style.setProperty('--card-color', card.colour || '#6B4C7D');

      var hero = el('a', 'scent-deck__tile-hero');
      hero.href = card.url;
      hero.appendChild(el('span', 'scent-deck__tile-name', card.name));
      tile.appendChild(hero);

      var remove = el('button', 'scent-deck__tile-remove');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Remove ' + card.name + ' from my scents');
      remove.innerHTML = HEART_SVG;
      remove.addEventListener('click', function () {
        onRemove(card, remove, items);
      });
      tile.appendChild(remove);

      var body = el('div', 'scent-deck__tile-body');
      if (card.subtitle) body.appendChild(el('p', 'scent-deck__tile-subtitle', card.subtitle));

      var state = buyState(card);
      state.links.forEach(function (l) {
        var a = el('a', 'scent-deck__tile-link', l.label);
        a.href = l.href;
        body.appendChild(a);
      });
      if (state.line) {
        body.appendChild(el('p', state.quiet ? 'scent-deck__tile-backsoon' : 'scent-deck__tile-seasonal', state.line));
      }
      tile.appendChild(body);
      return tile;
    }

    function render(items, notice) {
      setStatus(notice || null);
      grid.textContent = '';
      var saved = items
        .map(function (i) { return byGid[i.gid]; })
        .filter(function (c) { return !!c; });
      if (!saved.length) {
        if (emptyEl) emptyEl.hidden = false;
        grid.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      grid.hidden = false;
      saved.forEach(function (card) {
        grid.appendChild(makeTile(card, items));
      });
    }

    function onRemove(card, btn, items) {
      if (btn.disabled) return;
      var next = items.filter(function (i) { return i.gid !== card.gid; });
      localRemove(card.gid);
      render(next, null);
      if (customerId === null) return;
      btn.disabled = true;
      api('/remove', { gid: card.gid, slug: card.slug }).catch(function () {
        /* restore on failure */
        localAdd({ gid: card.gid, slug: card.slug });
        render(items, 'Could not save - tap to retry');
      });
    }

    if (customerId !== null) setStatus('Loading your scents...');

    reconcile(customerId)
      .then(function (res) {
        render(res.items, null);
      })
      .catch(function () {
        render(readState().items, 'Could not load your scents - showing your last saved list.');
      });
  }

  /* ---------------- boot ---------------- */

  document.addEventListener('DOMContentLoaded', function () {
    var card = document.querySelector('article[data-scent-gid][data-enable-save="true"]');
    if (card) initCard(card);
    var deck = document.querySelector('[data-scent-deck]');
    if (deck) initDeck(deck);
  });
})();
